import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setAccessToken } from '../redux/authSlice';
import { axiosInstance } from '../Axios/axios';
import { useLocation } from "react-router-dom";
import { jwtDecode } from 'jwt-decode'; // Install: npm install jwt-decode

export const useTokenValidator = () => {
    const dispatch = useDispatch();
    const accessToken = useSelector(state => state.auth.accessToken);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();
    const refreshTimeoutRef = useRef(null);

    // Function to schedule proactive refresh
    const scheduleTokenRefresh = (token) => {
        if (!token) return;

        try {
            const decoded = jwtDecode(token);
            const expiresAt = decoded.exp * 1000; // Convert to milliseconds
            const now = Date.now();
            
            // Refresh 1 minute before expiry (or immediately if less than 1 min left)
            const refreshTime = expiresAt - now - 60000;
            
            // Clear any existing timeout
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }

            if (refreshTime > 0) {
                console.log(`Token will refresh in ${Math.round(refreshTime / 1000)} seconds`);
                
                refreshTimeoutRef.current = setTimeout(async () => {
                    try {
                        const refreshResponse = await axiosInstance.post("/auth/refresh-token");
                        const newToken = refreshResponse.data.message.accessToken;
                        dispatch(setAccessToken({ accessToken: newToken }));
                        
                        // Schedule next refresh for the new token
                        scheduleTokenRefresh(newToken);
                    } catch (error) {
                        console.error("Proactive refresh failed:", error);
                        if (error.response?.status === 401) {
                            dispatch(logout());
                        }
                    }
                }, refreshTime);
            } else {
                // Token is about to expire or already expired, refresh now
                refreshTokenNow();
            }
        } catch (error) {
            console.error("Error decoding token:", error);
        }
    };

    const refreshTokenNow = async () => {
        try {
            const refreshResponse = await axiosInstance.post("/auth/refresh-token");
            const newToken = refreshResponse.data.message.accessToken;
            dispatch(setAccessToken({ accessToken: newToken }));
            scheduleTokenRefresh(newToken);
        } catch (error) {
            if (error.response?.status === 401) {
                dispatch(logout());
            }
        }
    };

    useEffect(() => {
        const proactiveRefresh = async () => {
            setIsLoading(true);

            // Skip if on login page
            if (location.pathname === "/login" || location.pathname === "/") {
                setIsLoading(false);
                return;
            }

            // If accessToken exists, schedule proactive refresh
            if (accessToken) {
                scheduleTokenRefresh(accessToken);
                setIsLoading(false);
                return;
            }

            // No accessToken, try to get one via refresh token
            try {
                const refreshResponse = await axiosInstance.post("/auth/refresh-token");
                const newToken = refreshResponse.data.message.accessToken;
                dispatch(setAccessToken({ accessToken: newToken }));
                scheduleTokenRefresh(newToken);
            } catch (error) {
                if (error.response?.status === 401) {
                    dispatch(logout());
                }
            } finally {
                setIsLoading(false);
            }
        };

        proactiveRefresh();

        // Cleanup timeout on unmount
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [dispatch, location.pathname]);

    // Re-schedule when token changes
    useEffect(() => {
        if (accessToken) {
            scheduleTokenRefresh(accessToken);
        }
    }, [accessToken]);

    return { isLoading, accessToken };
};